"use client";

import { useEffect, useMemo, useState } from "react";

import ProductGallery from "./ProductGallery.client";
import ProductDetailsPanel from "./ProductDetailsPanel.client";
import ProductDetailsSpecs from "./ProductDetailsSpecs.client";
import ProductDetailsMobileAddBar from "./ProductDetailsMobileAddBar.client";

type InfoItem = {
  label: string;
  value: string;
};

type InfoGroup = {
  variantId?: number;
  price?: string;
  group: string;
  title?: string;
  items: InfoItem[];
};

type ProductDetailsPurchaseLayoutProps = {
  product: {
    variantId: number;
    title: string;
    description: string;
    categoryLabel: string;
    isNew: boolean;
    rating: number | null;
    reviewsCount: number;
    stock: number;
    isPublic: boolean;
    price: string;
    originalPrice: string;
    discountPercentage: string;
    sku: string;
    image: string;
    gallery: string[];
  };
  isAvailable: boolean;
  availabilityLabel: string;
  infoGroups: InfoGroup[];
};

export default function ProductDetailsPurchaseLayout({
  product,
  isAvailable,
  availabilityLabel,
  infoGroups,
}: ProductDetailsPurchaseLayoutProps) {
  const selectableInfoGroups = useMemo(
    () =>
      infoGroups.filter(
        (group) => typeof group.variantId === "number" && group.variantId > 0,
      ),
    [infoGroups],
  );
  const selectableGroupIds = useMemo(
    () => selectableInfoGroups.map((group) => group.variantId as number),
    [selectableInfoGroups],
  );
  const selectableGroupKey = selectableGroupIds.join(",");
  const [selectedGroupIds, setSelectedGroupIds] =
    useState<number[]>(selectableGroupIds);
  const hasSelectableInfoGroups = selectableInfoGroups.length > 0;
  const isFullProductSelected =
    hasSelectableInfoGroups &&
    selectedGroupIds.length === selectableGroupIds.length;
  const selectedVariantIds = hasSelectableInfoGroups
    ? isFullProductSelected
      ? [product.variantId]
      : selectedGroupIds
    : [product.variantId];

  useEffect(() => {
    setSelectedGroupIds(selectableGroupIds);
  }, [selectableGroupKey]);

  const toggleGroupSelection = (variantId: number) => {
    setSelectedGroupIds((currentIds) =>
      currentIds.includes(variantId)
        ? currentIds.filter((id) => id !== variantId)
        : [...currentIds, variantId],
    );
  };

  const toggleAllGroups = () => {
    setSelectedGroupIds((currentIds) =>
      currentIds.length === selectableGroupIds.length ? [] : selectableGroupIds,
    );
  };

  return (
    <>
      <div className="flex min-w-0 flex-col gap-5 lg:hidden" data-reveal-group>
        <div className="min-w-0" data-reveal="left">
          <ProductGallery
            title={product.title}
            categoryLabel={product.categoryLabel}
            image={product.image}
            gallery={product.gallery}
            variantId={product.variantId}
          />
        </div>

        <div className="min-w-0" dir="rtl">
          <ProductDetailsPanel
            product={product}
            isAvailable={isAvailable}
            availabilityLabel={availabilityLabel}
            infoGroups={infoGroups}
            showInfoGroups={false}
            showSelectableGroups={false}
            selectedVariantIdsOverride={selectedVariantIds}
            hasSelectableInfoGroupsOverride={hasSelectableInfoGroups}
            isFullProductSelectedOverride={isFullProductSelected}
          />
        </div>

        <div className="min-w-0" data-reveal="left">
          <ProductDetailsSpecs
            infoGroups={infoGroups}
            selectedGroupIds={selectedGroupIds}
            selectableGroupIds={selectableGroupIds}
            onToggleGroup={toggleGroupSelection}
            onToggleAllGroups={toggleAllGroups}
          />
        </div>
      </div>

      <div
        className="relative hidden min-w-0 gap-5 lg:grid lg:[direction:ltr] lg:grid-cols-[1.08fr_0.92fr] lg:items-start"
        data-reveal-group
      >
        <div
          className="min-w-0 space-y-4 lg:col-start-1 lg:row-start-1"
          data-reveal="left"
        >
          <ProductGallery
            title={product.title}
            categoryLabel={product.categoryLabel}
            image={product.image}
            gallery={product.gallery}
            variantId={product.variantId}
          />

          <ProductDetailsSpecs
            infoGroups={infoGroups}
            selectedGroupIds={selectedGroupIds}
            selectableGroupIds={selectableGroupIds}
            onToggleGroup={toggleGroupSelection}
            onToggleAllGroups={toggleAllGroups}
          />
        </div>

        <div className="min-w-0 lg:col-start-2 lg:row-start-1" dir="rtl">
          <ProductDetailsPanel
            product={product}
            isAvailable={isAvailable}
            availabilityLabel={availabilityLabel}
            infoGroups={infoGroups}
            showInfoGroups={false}
            showSelectableGroups={false}
            selectedVariantIdsOverride={selectedVariantIds}
            hasSelectableInfoGroupsOverride={hasSelectableInfoGroups}
            isFullProductSelectedOverride={isFullProductSelected}
          />
        </div>
      </div>

      <ProductDetailsMobileAddBar
        productVariantId={product.variantId}
        selectedVariantIds={selectedVariantIds}
        isAvailable={isAvailable}
        hasSelectableInfoGroups={hasSelectableInfoGroups}
        isFullProductSelected={isFullProductSelected}
      />
    </>
  );
}
